import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KUDOS_EMOJIS, KUDOS_MAX, type Kudos as Card } from '@dxtech/shared';
import { call } from '../api/library.js';
import { useApp } from '../state/app.js';
import { Avatar, ModalFrame, Spinner } from '../ui/common.js';

// 칭찬·감사 카드 — 화면(월별 전체 + 받은 순위)과 홈 위젯(이번 달 최신 5장)
interface KudosMonth { month: string; items: Card[]; ranking: Array<{ upn: string; name: string; n: number }> }
interface Person { upn: string; name: string; dept: string; title: string }

const thisMonth = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()).slice(0, 7);
const fetchMonth = async (m: string) => (await (await call(`/kudos?month=${m}`)).json()) as KudosMonth;
const when = (iso: string) => new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const shiftMonth = (m: string, d: number) => { const [y, mm] = m.split('-').map(Number); const t = new Date(Date.UTC(y!, mm! - 1 + d, 1)); return t.toISOString().slice(0, 7); };

function useKudos(month: string) {
  return useQuery({ queryKey: ['kudos', month], queryFn: () => fetchMonth(month), refetchInterval: 120_000 });
}

/** 카드 한 장 */
function KudosCard({ k, month, canDelete }: { k: Card; month: string; canDelete: boolean }): JSX.Element {
  const qc = useQueryClient();
  const { flash } = useApp();
  const del = useMutation({
    mutationFn: () => call('/kudos/delete', { month, id: k.id }),
    onSuccess: () => { flash('카드를 삭제했습니다'); qc.invalidateQueries({ queryKey: ['kudos'] }); },
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
      <div style={{ fontSize: 26, lineHeight: '34px', width: 34, textAlign: 'center', flexShrink: 0 }} aria-hidden>{k.emoji}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, color: '#6B7280' }}>
          <b style={{ color: '#111827' }}>{k.fromName}</b> → <b style={{ color: '#1D4ED8' }}>{k.toName}</b>
          <span style={{ marginLeft: 8, color: '#9CA3AF' }}>{when(k.at)}</span>
          {canDelete && <button onClick={() => { if (confirm('이 카드를 삭제할까요?')) del.mutate(); }} disabled={del.isPending}
            style={{ marginLeft: 8, border: 'none', background: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', padding: 0 }}>삭제</button>}
        </div>
        <div style={{ fontSize: 14, color: '#1F2937', marginTop: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{k.message}</div>
      </div>
    </div>
  );
}

/** 보내기 모달 */
export function KudosSendModal({ onClose, toUpn = '' }: { onClose: () => void; toUpn?: string }): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const people = useQuery({ queryKey: ['kudos', 'people'], queryFn: async () => (await (await call('/kudos/people')).json()) as { items: Person[] }, staleTime: 600_000 });
  const [to, setTo] = useState(toUpn);
  const [emoji, setEmoji] = useState<string>(KUDOS_EMOJIS[0]);
  const [message, setMessage] = useState('');
  const send = useMutation({
    mutationFn: () => call('/kudos', { toUpn: to, emoji, message }),
    onSuccess: () => { flash('칭찬 카드를 보냈습니다'); qc.invalidateQueries({ queryKey: ['kudos'] }); onClose(); },
    onError: (e) => flash(e instanceof Error ? e.message : '보내기 실패'),
  });
  const others = (people.data?.items ?? []).filter((p) => p.upn.toLowerCase() !== me.upn.toLowerCase());
  const ok = !!to && message.trim().length > 0 && !send.isPending;
  return (
    <ModalFrame title="칭찬·감사 카드 보내기" sub="모든 임직원이 볼 수 있는 카드입니다" onClose={onClose}
      footer={<>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={onClose}>취소</button>
        <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={!ok} onClick={() => send.mutate()}>{send.isPending ? '보내는 중…' : '보내기'}</button>
      </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#374151' }}>받는 사람
          <select className="dx-select" value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">선택하세요</option>
            {others.map((p) => <option key={p.upn} value={p.upn}>{p.name} {p.title} · {p.dept}</option>)}
          </select>
        </label>
        <div style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#374151' }}>스티커
          <div style={{ display: 'flex', gap: 6 }}>
            {KUDOS_EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setEmoji(e)} aria-pressed={emoji === e}
                style={{ fontSize: 22, width: 44, height: 40, borderRadius: 8, cursor: 'pointer', background: emoji === e ? '#EFF4FE' : '#fff', border: `1px solid ${emoji === e ? '#2563EB' : '#E5E7EB'}` }}>{e}</button>
            ))}
          </div>
        </div>
        <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#374151' }}>메시지
          <textarea className="dx-textarea" rows={4} maxLength={KUDOS_MAX} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="예) 성일하이텍 통합테스트 야근 고생했어요!" />
          <span style={{ fontSize: 11.5, color: '#9CA3AF', fontWeight: 400, textAlign: 'right' }}>{message.length}/{KUDOS_MAX}</span>
        </label>
      </div>
    </ModalFrame>
  );
}

/** 홈 위젯 — 이번 달 최신 5장 */
export function KudosWidget(): JSX.Element {
  const { me, setScreen } = useApp();
  const month = thisMonth();
  const q = useKudos(month);
  const [open, setOpen] = useState(false);
  const items = q.data?.items ?? [];
  const isAdmin = me.roles.includes('CEO') || me.roles.includes('Admin');
  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>칭찬·감사 카드</div>
        <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>이번 달 {items.length}장</span>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setScreen('kudos')}>전체 보기</button>
        <button className="dx-btn dx-btn-primary dx-btn-sm" onClick={() => setOpen(true)}>카드 보내기</button>
      </div>
      {q.isLoading ? <div style={{ padding: 28, textAlign: 'center' }}><Spinner /></div>
        : items.length === 0 ? <div style={{ padding: 26, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>이번 달 첫 칭찬 카드를 보내 보세요 👏</div>
        : items.slice(0, 5).map((k) => <KudosCard key={k.id} k={k} month={month} canDelete={isAdmin || k.fromUpn.toLowerCase() === me.upn.toLowerCase()} />)}
      {open && <KudosSendModal onClose={() => setOpen(false)} />}
    </div>
  );
}

/** 칭찬 카드 화면 — 월 이동, 이달의 칭찬왕(받은 순위), 전체 카드 */
export function KudosScreen(): JSX.Element {
  const { me } = useApp();
  const [month, setMonth] = useState(thisMonth());
  const q = useKudos(month);
  const [open, setOpen] = useState(false);
  const isAdmin = me.roles.includes('CEO') || me.roles.includes('Admin');
  const d = q.data;
  const top = d?.ranking[0]?.n ?? 0;
  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 16, animation: 'dxfade .3s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="이전 달">‹</button>
        <div className="tnum" style={{ fontSize: 15, fontWeight: 700, minWidth: 90, textAlign: 'center' }}>{month.replace('-', '년 ')}월</div>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= thisMonth()} aria-label="다음 달">›</button>
        <button className="dx-btn dx-btn-primary dx-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setOpen(true)}>카드 보내기</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        <section className="dx-card" style={{ padding: 18, flex: '1 1 260px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>이달의 칭찬왕 🏆</div>
          {!d ? <Spinner /> : d.ranking.length === 0 ? <div style={{ color: '#9CA3AF', fontSize: 13 }}>아직 받은 사람이 없습니다</div> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {d.ranking.slice(0, 10).map((r, i) => (
                <div key={r.upn} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
                  <span className="tnum" style={{ width: 18, color: i < 3 ? '#B45309' : '#9CA3AF', fontWeight: 700 }}>{i + 1}</span>
                  <Avatar name={r.name} upn={r.upn} size={28} index={i} />
                  <span style={{ flex: 1, fontWeight: r.n === top ? 700 : 500 }}>{r.name}{r.n === top ? ' 👑' : ''}</span>
                  <span className="tnum" style={{ color: '#4B5563' }}>{r.n}장</span>
                </div>
              ))}
            </div>
          )}
        </section>
        <section className="dx-card" style={{ overflow: 'hidden', flex: '2 1 420px', minWidth: 0 }}>
          <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', fontSize: 15, fontWeight: 700 }}>
            카드 <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>총 {d?.items.length ?? 0}장</span>
          </div>
          {!d ? <div style={{ padding: 28, textAlign: 'center' }}><Spinner /></div>
            : d.items.length === 0 ? <div style={{ padding: 26, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>이 달에는 카드가 없습니다</div>
            : d.items.map((k) => <KudosCard key={k.id} k={k} month={month} canDelete={isAdmin || k.fromUpn.toLowerCase() === me.upn.toLowerCase()} />)}
        </section>
      </div>
      {open && <KudosSendModal onClose={() => setOpen(false)} />}
    </div>
  );
}
